import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileUp, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { BASE, call } from "./common";

const MODES = [["full", "Full Import (new + existing)"], ["info", "Product information only"], ["price", "Prices / costs only"], ["stock", "Stock & lead times only"]];
const COUNT_LABELS = [["rows", "Rows"], ["unique_skus", "Unique SKUs"], ["create", "New"], ["update", "Updated"], ["unchanged", "Unchanged"],
  ["skipped", "Skipped"], ["conflict", "Other supplier"], ["held_for_review", "Held for price review"], ["errors", "Errors"], ["warnings", "Warnings"], ["new_categories", "New categories"]];

export default function ImportDialog({ s, open, onClose, onQueued }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("full");
  const [pv, setPv] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = async (m = mapping) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    if (m) fd.append("mapping", JSON.stringify(m));
    const d = await call(() => api.post(`${BASE}/suppliers/${s.id}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
    setBusy(false);
    if (d) { setPv(d); setMapping(d.mapping); }
  };
  const apply = async () => {
    const d = await call(() => api.post(`${BASE}/suppliers/${s.id}/feeds/${pv.feed_id}/apply`, { mode: pv.mode }),
      (r) => (r.duplicate ? "A job of this type is already queued" : "Import queued — follow progress in History or the Scheduler"));
    if (d) { setPv(null); setFile(null); setMapping(null); onQueued(); }
  };
  const close = () => { setPv(null); setFile(null); setMapping(null); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="supplier-import-dialog">
        <DialogHeader>
          <DialogTitle>Import {s.name} CSV</DialogTitle>
          <DialogDescription>Step 1 is a dry run: nothing changes until you click Apply. Image and datasheet URLs are stored as links only — no files are downloaded or copied.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <label className="h-10 px-4 rounded-md border bg-white text-sm flex items-center gap-2 cursor-pointer hover:border-accent">
            <FileUp size={16} /> {file ? file.name : "Choose CSV…"}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPv(null); }} data-testid="supplier-import-file" />
          </label>
          <Select value={mode} onValueChange={(v) => { setMode(v); setPv(null); }}>
            <SelectTrigger className="w-64" data-testid="supplier-import-mode"><SelectValue /></SelectTrigger>
            <SelectContent>{MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => preview(null)} disabled={!file || busy} className="bg-steel text-white" data-testid="supplier-import-preview-btn">
            {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : null}Validate &amp; preview</Button>
        </div>
        {pv && <PreviewBody pv={pv} mapping={mapping} setMapping={setMapping} remap={() => preview(mapping)} busy={busy} />}
        {pv && (
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={apply} disabled={busy || !(pv.counts.create + pv.counts.update)} className="bg-accent text-steel hover:bg-amber-600 font-semibold" data-testid="supplier-import-apply-btn">
              <CheckCircle2 size={16} className="mr-1" />Apply {(pv.counts.create + pv.counts.update).toLocaleString()} change(s)</Button>
          </div>)}
      </DialogContent>
    </Dialog>
  );
}

function PreviewBody({ pv, mapping, setMapping, remap, busy }) {
  const [tab, setTab] = useState("changes");
  return (
    <div className="space-y-4" data-testid="supplier-import-preview">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {COUNT_LABELS.map(([k, l]) => (
          <div key={k} className="rounded-lg border bg-slate-50 p-2 text-center" data-testid={`preview-count-${k}`}>
            <div className="text-lg font-extrabold text-steel">{(pv.counts[k] ?? 0).toLocaleString()}</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
          </div>))}
      </div>
      <div className="flex gap-2 text-sm">
        {[["changes", "Sample changes"], ["issues", `Errors & warnings (${pv.issues.length})`], ["mapping", "Column mapping"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`preview-tab-${k}`}
            className={`px-3 py-1 rounded-full border ${tab === k ? "bg-steel text-white" : "bg-white"}`}>{l}</button>))}
      </div>
      {tab === "changes" && (
        <div className="max-h-80 overflow-y-auto space-y-2" data-testid="preview-samples">
          {pv.samples.length === 0 && <p className="text-sm text-muted-foreground">No product changes.</p>}
          {pv.samples.slice(0, 60).map((x) => (
            <div key={x.sku} className="rounded-lg border p-2 text-xs">
              <b className="font-mono">{x.sku}</b> <span className="uppercase text-[10px] font-bold text-accent">{x.action}</span>
              <div className="mt-1 grid gap-0.5">{Object.entries(x.changes).map(([k, [a, b]]) => (
                <div key={k} className="truncate"><span className="text-muted-foreground">{k}:</span> {a != null && <span className="line-through text-red-600 mr-1">{JSON.stringify(a).slice(0, 60)}</span>}<span className="text-emerald-700">{JSON.stringify(b).slice(0, 120)}</span></div>))}</div>
            </div>))}
        </div>)}
      {tab === "issues" && (
        <div className="max-h-80 overflow-y-auto text-xs divide-y rounded-lg border" data-testid="preview-issues">
          {pv.issues.length === 0 && <p className="p-3 text-muted-foreground">No problems found.</p>}
          {pv.issues.map((i, n) => (
            <div key={n} className="p-2 flex gap-2"><AlertTriangle size={13} className={i.level === "error" ? "text-red-600" : "text-amber-500"} />
              <span className="font-mono w-10">r{i.row}</span><span className="font-mono w-28 truncate">{i.sku}</span><span>{i.msg}</span></div>))}
        </div>)}
      {tab === "mapping" && (
        <div className="space-y-2" data-testid="preview-mapping">
          <div className="grid sm:grid-cols-2 gap-2">
            {pv.fields.map(([k, label]) => (
              <div key={k} className="flex items-center gap-2 text-sm">
                <span className="w-48 shrink-0">{label}</span>
                <Select value={mapping[k] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [k]: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="h-8" data-testid={`mapping-${k}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">— not mapped —</SelectItem>
                    {pv.headers.filter(Boolean).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>))}
          </div>
          <Button size="sm" variant="outline" onClick={remap} disabled={busy} data-testid="mapping-repreview-btn">Save mapping &amp; preview again</Button>
        </div>)}
    </div>
  );
}
